import { ColDef, GridOptions } from 'ag-grid-enterprise';
import {
  AG_GRID_BLOCK_LOAD_DEBOUNCE_MILLIS,
  AG_GRID_CELL_CURSOR_POINTER,
  AG_GRID_CELL_TEXT_ELLIPSIS,
  AG_GRID_COLUMN_FILTER_DEBOUNCE,
  AG_GRID_HYPERLINK_TEXT_STYLE,
  AG_GRID_MAX_BLOCKS_IN_CACHE,
  AG_GRID_TOOL_TIP_SHOW_DELAY,
  ComplianceStatus,
  CYBERSCORE_FILTER_VALUES,
  DATE_TIME_FORMATTER_TIMEZONE,
  DEFAULT_COL_DEF,
  DEFAULT_GRID_OPTIONS,
  DEFAULT_GRID_SIDE_BAR_TOOL_PANEL,
  DEFAULT_PAGE_SIZE,
  DeviceManagementType,
  PLATFORM_VALUES,
  SORT_ORDER,
  USER_STATUS
} from '@smarsh-cyber/shared/constants';
import { AggridCustomDropdownFilterComponent } from '@smarsh-cyber/shared/aggrid';
import {
  formatTimestamp,
  translateCyberscoreFiltersToKeyValue,
  translateDeviceManagementTypeToKeyValue,
  translatePlatformToKeyValue,
  translateUserStatusToKeyValue
} from '@smarsh-cyber/shared/utils';
import { getCyberScoreColorCssClass } from '../../utils/compliance/compliance.utils';
import { AggridOsComponent } from '../../aggrid/aggrid-os/aggrid-os.component';

export const COMPLIANCE_HISTORIC_DATA_URL = '/historic/data',
  COMPLIANCE_EXPORT_HISTORIC_REPORT_URL = `/export${COMPLIANCE_HISTORIC_DATA_URL}`,
  COMPLIANCE_REPORT_FILE_NAME = 'Compliance Report',
  COMPLIANCE_DEFAULT_SORT_COLUMN = 'device',
  COMPLIANCE_DEFAULT_SORT_ORDER = 'asc',
  COMPLIANCE_HISTORIC_DATA_DATE_QUERY_PARAM = 'date';

export const ComplianceActionKeys = {
  SET_COMPLIANCE_COLUMN_CONFIG_STATE: 'Set Compliance Column Config State'
} as const;

export const ComplianceStatusCellClassRules = {
  'compliance-device-status-pass': (params) =>
    params.value === ComplianceStatus.PASS,
  'compliance-device-status-fail': (params) =>
    params.value === ComplianceStatus.FAIL,
  'compliance-device-status-warning': (params) =>
    params.value === ComplianceStatus.WARNING
};

export const COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS = {
  ...AG_GRID_COLUMN_FILTER_DEBOUNCE,
  values: [
    ComplianceStatus.PASS,
    ComplianceStatus.FAIL,
    ComplianceStatus.WARNING
  ]
};

export const DEVICE_COMPLIANCE_SCORE_STATUS = {
  PASS: {
    MIN: 700,
    MAX: 800
  },
  WARNING: {
    MIN: 500,
    MAX: 699
  },
  FAIL: {
    MIN: 0,
    MAX: 499
  }
};

export const COMPLIANCE_GRID_WIDTH_DEFINITION = {
  FACTORS: {
    MIN: 100
  },
  TEXT: {
    MIN: 300
  },
  DATE: {
    MIN: 150
  }
};

export const COMPLIANCE_COLUMN_DEFS: ColDef[] = [
  {
    field: 'device',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Device Name',
    filter: 'agTextColumnFilter',
    tooltipField: 'device',
    floatingFilter: true,
    pinned: 'left',
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    cellStyle: AG_GRID_CELL_TEXT_ELLIPSIS
  },
  {
    field: 'user',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'User',
    filter: 'agTextColumnFilter',
    tooltipField: 'user',
    floatingFilter: true,
    pinned: 'left',
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    // cellRenderer: (params) => {
    //   let cellValue = params.value;
    //   if (params.data?.userStatus === USER_STATUS.IN_ACTIVE)
    //     cellValue = `<b>(INACTIVE)</b> ${params.value}`;
    //   return cellValue;
    // },
    cellStyle: AG_GRID_CELL_TEXT_ELLIPSIS
  },
  {
    field: 'userStatus',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'User Status',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE,
      values: translateUserStatusToKeyValue(USER_STATUS).filter(
        (userStatus) => userStatus !== USER_STATUS.NEW
      )
    },
    tooltipField: 'userStatus',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false
  },
  {
    field: 'description',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Description',
    filter: 'agTextColumnFilter',
    tooltipField: 'description',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    cellStyle: AG_GRID_CELL_TEXT_ELLIPSIS
  },
  {
    field: 'email',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'User Email',
    filter: 'agTextColumnFilter',
    tooltipField: 'email',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'entredaUsername',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Username',
    filter: 'agTextColumnFilter',
    tooltipField: 'entredaUsername',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    cellStyle: {
      ...AG_GRID_CELL_TEXT_ELLIPSIS,
      ...AG_GRID_HYPERLINK_TEXT_STYLE
    }
  },
  {
    field: 'junctionId',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Junction ID',
    filter: 'agTextColumnFilter',
    tooltipField: 'junctionId',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'repId',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Rep ID',
    filter: 'agTextColumnFilter',
    tooltipField: 'repId',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'mgrId',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'MGR ID',
    filter: 'agTextColumnFilter',
    tooltipField: 'mgrId',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'branchId',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Branch ID',
    filter: 'agTextColumnFilter',
    tooltipField: 'branchId',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'osjMgrId',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'OSJ MGR ID',
    filter: 'agTextColumnFilter',
    tooltipField: 'osjMgrId',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'office',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Office',
    filter: 'agTextColumnFilter',
    tooltipField: 'office',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    hide: true
  },
  {
    field: 'cyberRisk',
    headerName: 'CyberRisk Number',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: true,
    filter: 'agTextColumnFilter',
    floatingFilterComponent: AggridCustomDropdownFilterComponent,
    floatingFilterComponentParams: {
      filterList: translateCyberscoreFiltersToKeyValue(CYBERSCORE_FILTER_VALUES)
    },
    cellClass: (params) => getCyberScoreColorCssClass(params.value)
  },
  {
    field: 'deviceOs',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Device OS',
    filter: 'agSetColumnFilter',
    cellRenderer: AggridOsComponent,
    tooltipField: 'deviceOs',
    sortable: false,
    floatingFilter: true,
    suppressFloatingFilterButton: false,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE,
      values: translatePlatformToKeyValue(PLATFORM_VALUES)
    }
  },
  {
    field: 'osVersion',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'OS Version',
    tooltipField: 'osVersion',
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    sortable: false,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    cellStyle: AG_GRID_CELL_TEXT_ELLIPSIS,
    hide: true
  },
  {
    field: 'osVersionNumber',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'OS Version Number',
    tooltipField: 'osVersionNumber',
    floatingFilter: true,
    filter: 'agTextColumnFilter',
    sortable: false,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    cellStyle: AG_GRID_CELL_TEXT_ELLIPSIS,
    hide: true
  },
  {
    field: 'version',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Agent Version',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    sortable: false,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    hide: true
  },
  {
    field: 'managementModel',
    floatingFilter: true,
    sortable: false,
    filter: 'agSetColumnFilter',
    suppressFloatingFilterButton: false,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE,
      values: translateDeviceManagementTypeToKeyValue(DeviceManagementType)
    },
    hide: true
  },
  {
    field: 'passWord',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Password Policy',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'passWordPolicy',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'passwordAge',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Password Age',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'passwordAge',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'screenLock',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Screen Lock',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'screenLock',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'diskEncryption',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Disk Encryption',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'diskEncryption',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'antivirusEnabled',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Antivirus Enabled',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'antivirus',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'antivirusAutoUpdate',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Antivirus Auto Update',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'antivirus',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'antiSpyware',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Anti-Spyware',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'antiSpyware',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'firewall',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Firewall',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'firewall',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'peerToPeer',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Peer-To-Peer',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'peerToPeer',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'osAutoUpdates',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'OS Auto Update',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'osAutoUpdates',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'wifi',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'WiFi',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'wifi',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'vpnConnected',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'VPN Connected',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'vpnConnected',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'criticalUpdatesInstalled',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Critical Updates Installed',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'criticalUpdatesInstalled',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'nonCriticalUpdatesInstalled',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Non-Critical Updates Installed',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'nonCriticalUpdatesInstalled',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'osClass',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'OS Class',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'osClass',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'osSupported',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'OS Supported',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'osSupported',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'dataLeakage',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Data Leakage',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'dataLeakage',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'remediationDeclined',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Remediation Declined',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'remediationDeclined',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'phishing',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'Phishing',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'phishing',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'sat',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'SAT',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'sat',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'newProcesses',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'New Processes',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'newProcesses',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'newApplications',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.FACTORS.MIN,
    headerName: 'New Applications',
    filter: 'agSetColumnFilter',
    filterParams: COMPLIANCE_DEVICE_STATUS_FILTER_PARAMS,
    tooltipField: 'newApplications',
    floatingFilter: true,
    sortable: false,
    suppressFloatingFilterButton: false,
    cellClassRules: ComplianceStatusCellClassRules
  },
  {
    field: 'userEnrolledDate',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.DATE.MIN,
    headerName: 'User Enrolled Date',
    tooltipField: 'userEnrolledDate'
  },
  {
    field: 'deviceInstalledDate',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.DATE.MIN,
    headerName: 'Device Installed Date',
    tooltipField: 'deviceInstalledDate'
  },
  {
    field: 'lastConnectedDate',
    valueFormatter: DATE_TIME_FORMATTER_TIMEZONE,
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.DATE.MIN,
    headerName: 'Last Connected Date',
    tooltipField: 'lastConnectedDate'
  },
  // Specific to Failing Factor report
  {
    field: 'factorName',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Factor',
    filter: 'agTextColumnFilter',
    tooltipField: 'factorName',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'failureDate',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Failure Date',
    tooltipField: 'failureDate',
    valueFormatter: (node) => formatTimestamp(node?.value),
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'age',
    minWidth: COMPLIANCE_GRID_WIDTH_DEFINITION.TEXT.MIN,
    headerName: 'Age (Days)',
    filter: 'agNumberColumnFilter',
    tooltipField: 'age',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    },
    sort: SORT_ORDER.DESC,
    sortIndex: 0
  },
  {
    field: 'buName',
    headerName: 'Account',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    hide: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  },
  {
    field: 'portalBuId',
    headerName: 'Account ID',
    hide: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: {
      ...AG_GRID_COLUMN_FILTER_DEBOUNCE
    }
  }
];

export const COMPLIANCE_REPORT_COL_DEF: ColDef = {
  ...DEFAULT_COL_DEF,
  resizable: true,
  suppressMovable: false,
  wrapText: true,
  sortable: true,
  filter: 'agTextColumnFilter',
  cellStyle: AG_GRID_CELL_CURSOR_POINTER
};

export const COMPLIANCE_GRID_OPTIONS: GridOptions = {
  ...DEFAULT_GRID_OPTIONS,
  defaultColDef: COMPLIANCE_REPORT_COL_DEF,
  rowModelType: 'serverSide',
  suppressHorizontalScroll: false,
  columnDefs: [],
  blockLoadDebounceMillis: AG_GRID_BLOCK_LOAD_DEBOUNCE_MILLIS,
  tooltipShowDelay: AG_GRID_TOOL_TIP_SHOW_DELAY,
  maxBlocksInCache: AG_GRID_MAX_BLOCKS_IN_CACHE,
  cacheBlockSize: DEFAULT_PAGE_SIZE,
  sideBar: {
    toolPanels: [
      {
        ...DEFAULT_GRID_SIDE_BAR_TOOL_PANEL.toolPanels[0],
        toolPanelParams: {
          ...DEFAULT_GRID_SIDE_BAR_TOOL_PANEL.toolPanels[0].toolPanelParams,
          suppressColumnMove: true
        }
      }
    ],
    defaultToolPanel: '' // setting this defaultToolPanel to empty to toggle the Columns toolbar
  },
  onCellClicked: (event) => {
    if (event.column.getColId() === 'entredaUsername') {
      event.event.stopPropagation(); // Stop the event from reaching onRowClicked
    }
  }
};
